import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const REPORT_TYPES = [
  { value: 'monthly_summary', label: 'Monthly Summary' },
  { value: 'annual_summary', label: 'Annual Summary' },
  { value: 'income_report', label: 'Income Report' },
  { value: 'expense_report', label: 'Expense Report' },
  { value: 'category_report', label: 'Category Report' },
  { value: 'budget_performance', label: 'Budget Performance' },
  { value: 'loan_report', label: 'Loan Report' },
  { value: 'credit_report', label: 'Credit Report' },
  { value: 'cash_flow_report', label: 'Cash Flow Report' },
  { value: 'allocation_report', label: 'Allocation Report' },
];

function money(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function currentMonth() {
  return String(new Date().getMonth() + 1);
}

function currentYear() {
  return String(new Date().getFullYear());
}

function ChartBars({ items, valueKey, labelKey = 'month', color = 'bg-emerald-600', currency = 'UGX' }) {
  const max = Math.max(...(items || []).map((item) => Math.abs(Number(item?.[valueKey] || 0))), 1);
  return (
    <div className="flex items-end gap-2 h-52 overflow-x-auto pb-2">
      {(items || []).map((item) => {
        const value = Number(item?.[valueKey] || 0);
        const height = Math.max(8, (Math.abs(value) / max) * 100);
        return (
          <div key={item[labelKey] || `${value}-${Math.random()}`} className="min-w-[56px] flex flex-col items-center justify-end gap-2">
            <div className="text-[11px] text-slate-500 leading-none">{money(value, currency).replace(`${currency} `, '')}</div>
            <div className="w-6 h-32 flex items-end">
              <div className={`w-full rounded-t-lg ${color}`} style={{ height: `${height}%` }} />
            </div>
            <div className="text-[10px] text-slate-500 leading-tight text-center whitespace-nowrap">{String(item[labelKey] || '').slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function BudgetReports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    type: 'monthly_summary',
    month: currentMonth(),
    year: currentYear(),
    branch_id: 'all',
  });

  const load = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        type: nextFilters.type || 'monthly_summary',
        month: nextFilters.month || undefined,
        year: nextFilters.year || undefined,
        branch_id: nextFilters.branch_id && nextFilters.branch_id !== 'all' ? nextFilters.branch_id : undefined,
      };
      const res = await api.get('/budget/reports', { params });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to load reports');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branches = useMemo(() => Array.isArray(data?.report?.branches) ? data.report.branches : [], [data?.report?.branches]);
  const activeBranches = useMemo(() => branches.filter((branch) => String(branch?.status || 'active').toLowerCase() === 'active'), [branches]);
  const singleBranch = activeBranches.length <= 1 || !!data?.report?.single_branch;
  const report = data?.report || {};
  const currency = user?.default_currency || 'UGX';

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    load(next);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {
        type: filters.type || 'monthly_summary',
        format: 'csv',
        month: filters.month || undefined,
        year: filters.year || undefined,
        branch_id: filters.branch_id && filters.branch_id !== 'all' ? filters.branch_id : undefined,
      };
      const res = await api.get('/budget/reports/export', { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budget-report-${filters.type}-${filters.year}-${filters.month}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (data?.upgrade_required) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Reports</h1>
          <p className="text-sm text-slate-500">Upgrade your package to unlock Budget and Financial Intelligence.</p>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">{data?.message || 'Budget access is required.'}</div>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => window.location.assign('/dashboard/package')}>View Packages</Button>
        </CardContent>
      </Card>
    );
  }

  const summaryCards = Array.isArray(report.summary_cards) ? report.summary_cards : [];
  const tables = Array.isArray(report.tables) ? report.tables : [];
  const charts = report.charts || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Reports</h1>
          <p className="text-sm text-slate-500 mt-0.5">Generate monthly, annual, and financial intelligence reports.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-200" onClick={() => window.print()}>Print</Button>
          <Button variant="outline" className="border-slate-200" onClick={exportCsv} disabled={exporting}>{exporting ? 'Exporting...' : 'CSV Export'}</Button>
        </div>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          <Select value={filters.type} onValueChange={(value) => updateFilter('type', value)}>
            <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Report type" /></SelectTrigger>
            <SelectContent>
              {REPORT_TYPES.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" min="1" max="12" value={filters.month} onChange={(e) => updateFilter('month', e.target.value)} placeholder="Month" className="bg-white" />
          <Input type="number" min="2020" max="2100" value={filters.year} onChange={(e) => updateFilter('year', e.target.value)} placeholder="Year" className="bg-white" />
          {!singleBranch ? (
            <Select value={filters.branch_id} onValueChange={(value) => updateFilter('branch_id', value)}>
              <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-600">{activeBranches[0]?.name || 'Main Branch'}</div>
          )}
          <div className="flex items-center h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-xs text-slate-500">Print-friendly report and CSV export are ready.</div>
        </CardContent>
      </Card>

      {summaryCards.length === 0 && tables.length === 0 && !charts.monthly_series?.length && !charts.cash_flow?.length ? (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-8 text-center text-slate-500">
            No report data yet for the selected period. Try a different month, year, or report type.
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {summaryCards.map((card) => (
          <Card key={card.label} className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">{card.label}</p>
              <p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{typeof card.value === 'number' ? money(card.value, currency) : card.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {charts.monthly_series?.length ? (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Trend</CardTitle></CardHeader>
          <CardContent><ChartBars items={charts.monthly_series} valueKey="net" labelKey="month" currency={currency} color="bg-blue-600" /></CardContent>
        </Card>
      ) : null}

      {charts.cash_flow?.length ? (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Cash Flow Comparison</CardTitle></CardHeader>
          <CardContent><ChartBars items={charts.cash_flow} valueKey="net" labelKey="month" currency={currency} color="bg-emerald-600" /></CardContent>
        </Card>
      ) : null}

      {Array.isArray(report.insights) && report.insights.length ? (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Insights</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {report.insights.map((item, index) => <div key={index} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">{item}</div>)}
          </CardContent>
        </Card>
      ) : null}

      {Array.isArray(report.warnings) && report.warnings.length ? (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Alerts</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {report.warnings.map((item, index) => (
              <div key={index} className={`rounded-xl border p-3 text-sm ${String(item.type) === 'danger' ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                <div className="font-semibold">{item.title}</div>
                <div>{item.message}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {tables.map((table) => (
        <Card key={table.title} className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{table.title}</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  {table.rows?.[0] ? Object.keys(table.rows[0]).map((key) => <th key={key} className="py-3 pr-4 font-medium">{key}</th>) : null}
                </tr>
              </thead>
              <tbody>
                {(table.rows || []).map((row, index) => (
                  <tr key={index} className="border-b border-slate-50 last:border-0">
                    {Object.keys(table.rows[0] || {}).map((key) => (
                      <td key={key} className="py-3 pr-4 text-slate-700">
                        {typeof row[key] === 'number' ? money(row[key], currency) : String(row[key] ?? '-')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      {report.health ? (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Financial Health</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="rounded-2xl border border-slate-200 p-4"><p className="text-sm text-slate-500">Score</p><p className="mt-2 text-2xl font-bold text-slate-900">{report.health.score}</p></div>
            <div className="rounded-2xl border border-slate-200 p-4"><p className="text-sm text-slate-500">Status</p><p className="mt-2 text-2xl font-bold text-slate-900">{report.health.status}</p></div>
            <div className="rounded-2xl border border-slate-200 p-4"><p className="text-sm text-slate-500">Tone</p><p className="mt-2 text-2xl font-bold text-slate-900">{report.health.tone}</p></div>
            <div className="rounded-2xl border border-slate-200 p-4"><p className="text-sm text-slate-500">Advice</p><p className="mt-2 text-sm text-slate-700">{(report.health.advice || [])[0] || '-'}</p></div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
